import React, { useState } from 'react';
import './RegistrationModal.css';
import { AuthService } from '../services/auth.service';
import { IRegisterDto } from '../types/auth.types';
import { isRegistrationOpen } from '../config/featureFlags';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShowLogin: () => void;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({ isOpen, onClose, onShowLogin }) => {
  const [formData, setFormData] = useState<IRegisterDto>({
    // Информация о капитане
    captainFullName: '',
    captainEmail: '',
    captainPhone: '',
    captainBirthDate: '',
    
    // Информация о команде
    commandName: '',
    organization: '',
    city: '',
    
    // Участник 1
    member1FullName: '',
    member1Email: '',
    member1Phone: '',
    member1BirthDate: '',
    
    // Участник 2 (опциональный)
    member2FullName: '',
    member2Email: '',
    member2Phone: '',
    member2BirthDate: '',
    
    // Участник 3 (опциональный)
    member3FullName: '',
    member3Email: '',
    member3Phone: '',
    member3BirthDate: '',
    
    // Системные поля
    password: '',
    hasConsentPersonalData: false
  });

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isRegistrationOpen()) {
      setError('Регистрация в данный момент недоступна');
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      await AuthService.register(formData);
      setIsSuccess(true);
      setFormData({
        // Информация о капитане
        captainFullName: '',
        captainEmail: '',
        captainPhone: '',
        captainBirthDate: '',
        
        // Информация о команде
        commandName: '',
        organization: '',
        city: '',
        
        // Участник 1
        member1FullName: '',
        member1Email: '',
        member1Phone: '',
        member1BirthDate: '',
        
        // Участник 2 (опциональный)
        member2FullName: '',
        member2Email: '',
        member2Phone: '',
        member2BirthDate: '',
        
        // Участник 3 (опциональный)
        member3FullName: '',
        member3Email: '',
        member3Phone: '',
        member3BirthDate: '',
        
        // Системные поля
        password: '',
        hasConsentPersonalData: false
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при регистрации');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginClick = () => {
    onClose();
    onShowLogin();
  };

  if (!isOpen || !isRegistrationOpen()) return null;

  if (isSuccess) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="success-message">
            <h2>Регистрация успешна!</h2>
            <p>Вы успешно зарегистрировались. Теперь вы можете войти в систему.</p>
            <div className="modal-buttons">
              <button 
                className="submit-button"
                onClick={handleLoginClick}
              >
                Войти
              </button>
              <button 
                className="cancel-button"
                onClick={onClose}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>Регистрация команды</h2>
        <form onSubmit={handleSubmit}>
          <h3>Информация о капитане команды</h3>
          <div className="form-group">
            <label>
              ФИО капитана*
              <input
                type="text"
                name="captainFullName"
                value={formData.captainFullName}
                onChange={handleInputChange}
                required
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Email капитана*
              <input
                type="email"
                name="captainEmail"
                value={formData.captainEmail}
                onChange={handleInputChange}
                required
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Телефон капитана*
              <input
                type="tel"
                name="captainPhone"
                value={formData.captainPhone}
                onChange={handleInputChange}
                required
                placeholder="+79001234567"
                pattern="\+7[0-9]{10}"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Дата рождения капитана*
              <input
                type="date"
                name="captainBirthDate"
                value={formData.captainBirthDate}
                onChange={handleInputChange}
                required
              />
            </label>
          </div>

          <h3>Информация о команде</h3>
          <div className="form-group">
            <label>
              Название команды*
              <input
                type="text"
                name="commandName"
                value={formData.commandName}
                onChange={handleInputChange}
                required
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Образовательная организация*
              <input
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                required
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Город*
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          <h3>Участник 1</h3>
          <div className="form-group">
            <label>
              ФИО участника 1*
              <input
                type="text"
                name="member1FullName"
                value={formData.member1FullName}
                onChange={handleInputChange}
                required
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Email участника 1*
              <input
                type="email"
                name="member1Email"
                value={formData.member1Email}
                onChange={handleInputChange}
                required
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Телефон участника 1*
              <input
                type="tel"
                name="member1Phone"
                value={formData.member1Phone}
                onChange={handleInputChange}
                required
                placeholder="+79001234567"
                pattern="\+7[0-9]{10}"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Дата рождения участника 1*
              <input
                type="date"
                name="member1BirthDate"
                value={formData.member1BirthDate}
                onChange={handleInputChange}
                required
              />
            </label>
          </div>

          <h3>Участник 2 (необязательно)</h3>
          <div className="form-group">
            <label>
              ФИО участника 2
              <input
                type="text"
                name="member2FullName"
                value={formData.member2FullName}
                onChange={handleInputChange}
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Email участника 2
              <input
                type="email"
                name="member2Email"
                value={formData.member2Email}
                onChange={handleInputChange}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Телефон участника 2
              <input
                type="tel"
                name="member2Phone"
                value={formData.member2Phone}
                onChange={handleInputChange}
                placeholder="+79001234567"
                pattern="\+7[0-9]{10}"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Дата рождения участника 2
              <input
                type="date"
                name="member2BirthDate"
                value={formData.member2BirthDate}
                onChange={handleInputChange}
              />
            </label>
          </div>

          <h3>Участник 3 (необязательно)</h3>
          <div className="form-group">
            <label>
              ФИО участника 3
              <input
                type="text"
                name="member3FullName"
                value={formData.member3FullName}
                onChange={handleInputChange}
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Email участника 3
              <input
                type="email"
                name="member3Email"
                value={formData.member3Email}
                onChange={handleInputChange}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Телефон участника 3
              <input
                type="tel"
                name="member3Phone"
                value={formData.member3Phone}
                onChange={handleInputChange}
                placeholder="+79001234567"
                pattern="\+7[0-9]{10}"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Дата рождения участника 3
              <input
                type="date"
                name="member3BirthDate"
                value={formData.member3BirthDate}
                onChange={handleInputChange}
              />
            </label>
          </div>

          <h3>Системные поля</h3>
          <div className="form-group">
            <label>
              Пароль*
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={6}
                maxLength={100}
              />
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="hasConsentPersonalData"
                checked={formData.hasConsentPersonalData}
                onChange={e => setFormData(prev => ({ ...prev, hasConsentPersonalData: e.target.checked }))}
                required
              />
              Заполняя настоящую форму, в соответствии с требованиями статьи 9 Федерального закона от 27.07.2006 № 152-ФЗ «О персональных данных», я подтверждаю своё согласие на обработку вносимых в форму моих персональных данных (галочка или «Да»)*
            </label>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-buttons">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Регистрация...' : 'Зарегистрировать команду'}
            </button>
            <button 
              type="button"
              className="cancel-button"
              onClick={onClose}
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationModal; 